import { Injectable } from '@angular/core';
import { TGuid } from '@core/helpers';
import { BehaviorSubject, merge, Observable, of, Subject, throwError } from 'rxjs';
import {
  IObservationFull,
  IRequisitionParticipant,
  IRequisitionUpdateEvent,
  IRequisitionWithQuestionsAnswers,
  ISummaryNote,
  ISummaryNoteHistory,
  INewMedicalRecord,
  IMedicalRecord,
  EProfileTypeDTO,
} from '@project/view-models';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ObservationsApiProviderService,
  RequisitionApiProviderService,
  SummaryNotesApiProviderService,
  SummaryApiProviderService,
} from '@project/data-providers';
import { TranslateService } from '@project/translate';
import { ISummaryNotesManagerService } from '@modules';
import { BroadcastEventsService } from '@core/services';
import { CloseAsidePageEvent, EAsideOutlets } from '@project/shared';
import {
  AuthorisationStateService,
  RequisitionHelpersService,
  RequisitionIsUpdatedEventFeatureService,
} from '@project/services';
import { MeasurementPreview } from 'src/app/project/modules/session-details-shared/session-details-shared-patient-observations/session-details-shared-patient-observations.component';
import { LocalStorageService } from 'src/app/project/services/local-storage.service';

@Injectable()
export class RequisitionDetailsManagerService implements ISummaryNotesManagerService<IRequisitionWithQuestionsAnswers> {
  private readonly _notes$ = new BehaviorSubject<ISummaryNote[]>([]);
  public readonly notes$ = this._notes$;

  private readonly _medicalRecords$ = new BehaviorSubject<IMedicalRecord[]>([]);
  public readonly medicalRecords = this._medicalRecords$;

  private readonly _requisition$ = new BehaviorSubject<IRequisitionWithQuestionsAnswers>(null);
  public readonly requisition$ = this._requisition$.asObservable();

  private readonly _measurements$ = new BehaviorSubject<MeasurementPreview>(null);
  public readonly measurments$ = this._measurements$.asObservable();

  public readonly requisitionParticipant$: Observable<IRequisitionParticipant | null> = this.requisition$.pipe(
    map((requisition) => this.requisitionHelpersService.getRequisitionParticipantForCurrentUser(requisition)),
  );

  private readonly _observations$ = new BehaviorSubject<IObservationFull[]>([]);
  public readonly observations$ = this._observations$.asObservable();

  /**
   * TODO Need some logic in future according to new requisition Flow.
   * This logic is unclear for 2020-09-22
   */
  private _showCloseRequisitionButton$ = new BehaviorSubject(true);
  public showCloseRequisitionButton$ = this._showCloseRequisitionButton$.asObservable();

  private destroyed$ = new Subject();

  constructor(
    private summaryNotesApiProviderService: SummaryNotesApiProviderService,
    private summaryApiProviderService: SummaryApiProviderService,
    private requisitionApiProviderService: RequisitionApiProviderService,
    private notificationsService: NotificationsService,
    private observationsApiProviderService: ObservationsApiProviderService,
    private requisitionHelpersService: RequisitionHelpersService,
    private requisitionIsUpdatedEventFeatureService: RequisitionIsUpdatedEventFeatureService,
    private broadcastEventsService: BroadcastEventsService,
    private authorisationStateService: AuthorisationStateService,
    private localStorageService: LocalStorageService,
  ) {}
  getHistoryById(id: string): Observable<ISummaryNoteHistory[]> {
    throw new Error('Method not implemented.');
  }
  updateNote(id: string, note: string): Observable<void> {
    throw new Error('Method not implemented.');
  }

  loadMeasurement(): Observable<void> {
    const requisition = this._requisition$.value;

    if (!requisition) {
      return of(null);
    }

    return this.observationsApiProviderService
      .getFullMeasurementByPatientIdAsHcEmployer(requisition.patientProfileId)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          let errorMessage = 'Error measurements patient';
          if (typeof err?.error === 'string') {
            errorMessage = err?.error;
          }

          this.notificationsService.error({
            message: errorMessage,
          });
          return of([]);
        }),
        tap((m: MeasurementPreview) => {
          this._measurements$.next(m);
        }),
        map(() => null),
      );
  }

  init() {
    this.requisitionIsUpdatedEventFeatureService.requisitionUpdated$
      .pipe(
        filter((event) => this._requisition$.value?.id === event.requisitionId),
        switchMap((event) => this.requisitionApiProviderService.getById(event.requisitionId)),
        takeUntil(this.destroyed$),
      )
      .subscribe((requisition) => {
        const currentRequisition = this._requisition$.value;
        if (currentRequisition) {
          this._requisition$.next({
            ...requisition,
            questions: currentRequisition.questions,
          });
        }
      });

    merge(
      this.requisitionIsUpdatedEventFeatureService.requisitionClosed$.pipe(
        filter((requisitionId) => {
          const currentRequisition = this._requisition$.value;
          return currentRequisition.id === requisitionId;
        }),
      ),
      this.requisitionIsUpdatedEventFeatureService.requisitionUpdated$.pipe(
        filter((updateEvent: IRequisitionUpdateEvent) => {
          const currentRequisition = this._requisition$.value;
          return currentRequisition.id === updateEvent.requisitionId;
        }),
      ),
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const isDoctorSaving = this.localStorageService.getItem('isDoctorSaving') === 'true';

        // Avoids aside closing and preventing the doctor from finishing the sumary notes
        if (
          !isDoctorSaving &&
          this.authorisationStateService.getAuthorizationData().userType === EProfileTypeDTO.Doctor
        ) {
          this.notificationsService.info({
            message: TranslateService.localize('summary-notes.requisition-closed-message'),
          });

          const doctorIsAway = new CustomEvent('doctor-is-away', { detail: { isAway: true } });
          window.dispatchEvent(doctorIsAway);

          return;
        }

        this.broadcastEventsService.broadcast(new CloseAsidePageEvent(EAsideOutlets.GlobalAsideOutlet));
      });
  }

  destroy() {
    this.destroyed$.next();
  }

  loadMedicalRecords(): Observable<void> {
    const requisition = this._requisition$.value;
    if (!requisition) {
      return of(null);
    }

    if (this.authorisationStateService.getAuthorizationData().userType === EProfileTypeDTO.Patient) {
      return null;
    }

    return this.summaryApiProviderService.getMedicalRecordsById(requisition.id, requisition.doctors[0].id).pipe(
      map((medicalRecords) => {
        const sortedMedicalRecords = medicalRecords.map((medicalRecord) => ({
          ...medicalRecord,
          created_at: (new Date(medicalRecord.create_at) as unknown) as string,
        }));

        sortedMedicalRecords.sort((a, b) => (a.created_at > b.created_at ? -1 : a.created_at < b.created_at ? 1 : 0));

        return this._medicalRecords$.next(sortedMedicalRecords);
      }),
    );
  }

  loadNotes(): Observable<void> {
    const requisition = this._requisition$.value;

    if (!requisition) {
      return of(null);
    }

    return this.summaryNotesApiProviderService.getAllRelatedToPatient(requisition.patientProfileId).pipe(
      catchError((err: HttpErrorResponse) => {
        let errorMessage = TranslateService.localize('summary-notes.load-notes-fail-message');

        if (typeof err?.error === 'string') {
          errorMessage = err?.error;
        }

        this.notificationsService.error({
          message: errorMessage,
        });
        return of([]);
      }),
      tap((notes) => {
        this._notes$.next(notes);
      }),
      map(() => null),
    );
  }

  loadRequisition(requisitionId: TGuid): Observable<void> {
    return this.requisitionApiProviderService.getWithAnswersById(requisitionId).pipe(
      catchError((err: HttpErrorResponse) => {
        this.notificationsService.error({
          message: TranslateService.localize('session.details.errors.unable-to-load-requisition'),
        });
        return throwError(err);
      }),
      tap((requisition) => this._requisition$.next(requisition)),
      map(() => null),
    );
  }

  loadObservations(): Observable<void> {
    const requisition = this._requisition$;

    if (!requisition) {
      return of(null);
    }

    return this.observationsApiProviderService
      .getFullObservationsByPatientIdAsHcEmployer(requisition.value.patientProfileId)
      .pipe(
        catchError(() => {
          this.notificationsService.error({
            message: TranslateService.localize('observations.details-load-fail-message'),
          });
          return of([]);
        }),
        tap((observations) => this._observations$.next(observations)),
        map(() => null),
      );
  }

  createNote(note: string): Observable<void> {
    return this.summaryNotesApiProviderService.createByRequisitionId(this._requisition$.value.id, note).pipe(
      catchError((err) => {
        let errorMessage = TranslateService.localize('summary-notes.create-note-fail-message');

        if (typeof err?.error === 'string') {
          errorMessage = err?.error;
        }

        this.notificationsService.error({
          message: errorMessage,
        });

        return throwError(err);
      }),
      switchMap(() => this.loadNotes()),
      map(() => null),
    );
  }

  createMedicalRecord(medicalRecord: INewMedicalRecord): Observable<void> {
    return this.summaryApiProviderService.createMedicalRecord(medicalRecord).pipe(
      catchError((err) => {
        this.notificationsService.error({
          // TODO: translate
          message: TranslateService.localize('summary-notes.create-note-fail-message'),
        });
        return throwError(err);
      }),
      switchMap(() => this.loadNotes()),
      map(() => null),
    );
  }
}
